import React, { useState } from 'react';
import { Users, Check, Edit3, Trophy, Star, Award, Crown, Sparkles } from 'lucide-react';

// === Sample data ===
const GROUPS = [
  {
    name: 'א',
    players: ['אלדד שמואלי', 'דרור טל', 'אריה קורז׳בסקי', 'עילי הר לב'],
    matches: [
      { p1: 'אלדד שמואלי', p1Seed: 1, p2: 'דרור טל', p1Scores: [6, 6], p2Scores: [3, 4], winner: 1, completed: true },
      { p1: 'אריה קורז׳בסקי', p2: 'עילי הר לב', p1Scores: [4, 6, 3], p2Scores: [6, 3, 6], winner: 2, completed: true },
      { p1: 'אלדד שמואלי', p1Seed: 1, p2: 'אריה קורז׳בסקי', completed: false },
    ]
  },
  {
    name: 'ב',
    players: ['שי גני', 'חיים דואני', 'TOM GELLERT', 'חי נאור'],
    matches: [
      { p1: 'שי גני', p1Seed: 3, p2: 'חיים דואני', p1Scores: [6, 6], p2Scores: [2, 1], winner: 1, completed: true },
      { p1: 'TOM GELLERT', p2: 'חי נאור', p1Scores: [7, 6], p2Scores: [5, 4], winner: 1, completed: true },
      { p1: 'שי גני', p1Seed: 3, p2: 'TOM GELLERT', completed: false },
    ]
  },
];

// ============================================================
// OPTION 1 — Stadium Premium (אצטדיון פרימיום)
// ============================================================
const StadiumPremium = () => (
  <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-8 rounded-2xl" dir="rtl">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-blue-200 rounded-full shadow-sm mb-2">
        <Sparkles className="w-4 h-4 text-blue-500" />
        <span className="text-xs font-bold text-blue-700 tracking-wider">GROUP STAGE</span>
      </div>
      <h2 className="text-3xl font-black text-slate-900">🎾 שלב הבתים</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {GROUPS.map((group, gIdx) => (
        <div key={gIdx} className="bg-white rounded-2xl overflow-hidden shadow-xl shadow-blue-100/50 border border-slate-100">
          {/* Header with gradient + decorative blob */}
          <div className="relative h-20 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/5 rounded-full" />
            <div className="relative h-full flex items-center justify-between px-6">
              <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-1.5">
                <p className="text-white/80 text-xs font-bold tracking-widest">GROUP</p>
                <p className="text-white text-2xl font-black">{group.name}</p>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-xs">{group.players.length} שחקנים</p>
                <p className="text-white text-sm font-bold">{group.matches.length} משחקים</p>
              </div>
            </div>
          </div>
          
          {/* Players */}
          <div className="px-6 py-4 bg-gradient-to-b from-blue-50/50 to-white border-b border-slate-100">
            <p className="text-xs font-bold text-slate-500 tracking-wider mb-3">SQUAD</p>
            <div className="grid grid-cols-2 gap-2">
              {group.players.map((p, i) => (
                <div key={i} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-100 hover:border-blue-300 transition-colors">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-black flex items-center justify-center shadow-md">
                    {i + 1}
                  </div>
                  <span className="text-sm font-semibold text-slate-700 truncate">{p}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Matches */}
          <div className="p-4 space-y-3">
            <p className="text-xs font-bold text-slate-500 tracking-wider px-2">FIXTURES</p>
            {group.matches.map((m, i) => (
              <div key={i} className={`relative bg-gradient-to-r ${
                m.completed ? 'from-emerald-50 via-white to-emerald-50 border-emerald-200' : 'from-slate-50 via-white to-slate-50 border-slate-200'
              } border rounded-xl p-3 hover:shadow-md transition-all`}>
                {/* Winner accent */}
                {m.completed && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-500 rounded-t-xl" />
                )}
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                  {/* Player 1 */}
                  <div className={`text-right ${m.winner === 1 ? 'text-emerald-700' : 'text-slate-700'}`}>
                    <div className="flex items-center justify-end gap-2">
                      {m.p1Seed && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-bold">#{m.p1Seed}</span>}
                      <span className={`text-sm ${m.winner === 1 ? 'font-bold' : 'font-medium'}`}>{m.p1}</span>
                      {m.winner === 1 && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
                    </div>
                    {m.p1Scores && (
                      <div className="flex justify-end gap-1 mt-1">
                        {m.p1Scores.map((s, idx) => (
                          <span key={idx} className={`w-6 h-6 flex items-center justify-center rounded-md text-xs font-mono font-bold ${
                            m.winner === 1 ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                          }`}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* VS */}
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black ${
                      m.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                    }`}>VS</div>
                  </div>
                  
                  {/* Player 2 */}
                  <div className={`text-left ${m.winner === 2 ? 'text-emerald-700' : 'text-slate-700'}`}>
                    <div className="flex items-center gap-2">
                      {m.winner === 2 && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
                      <span className={`text-sm ${m.winner === 2 ? 'font-bold' : 'font-medium'}`}>{m.p2}</span>
                    </div>
                    {m.p2Scores && (
                      <div className="flex gap-1 mt-1">
                        {m.p2Scores.map((s, idx) => (
                          <span key={idx} className={`w-6 h-6 flex items-center justify-center rounded-md text-xs font-mono font-bold ${
                            m.winner === 2 ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                          }`}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// OPTION 2 — Tennis Scorecard (כרטיס ניקוד טניס קלאסי)
// ============================================================
const TennisScorecard = () => (
  <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-8 rounded-2xl" dir="rtl">
    <div className="text-center mb-6">
      <h2 className="text-3xl font-black text-emerald-900">🎾 לוח ניקוד הבתים</h2>
      <p className="text-emerald-700 text-sm mt-1">Tournament Scorecard</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {GROUPS.map((group, gIdx) => (
        <div key={gIdx} className="bg-white rounded-xl overflow-hidden shadow-lg border-2 border-emerald-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-green-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-black text-emerald-700 text-lg shadow-lg">
                {group.name}
              </div>
              <div>
                <p className="text-white text-lg font-bold">בית {group.name}</p>
                <p className="text-emerald-100 text-xs">Round Robin</p>
              </div>
            </div>
            <div className="text-left">
              <p className="text-emerald-200 text-xs">{group.matches.length}/6</p>
              <div className="w-20 h-1.5 bg-emerald-800/50 rounded-full mt-1">
                <div className="h-full bg-white rounded-full" style={{ width: `${(group.matches.filter(m=>m.completed).length / 6) * 100}%` }} />
              </div>
            </div>
          </div>
          
          {/* Standings Table */}
          <div className="bg-emerald-50/50 p-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-emerald-700 text-xs">
                  <th className="text-right py-1">#</th>
                  <th className="text-right py-1">שחקן</th>
                  <th className="text-center py-1">נצ׳</th>
                  <th className="text-center py-1">הפ׳</th>
                </tr>
              </thead>
              <tbody>
                {group.players.map((p, i) => (
                  <tr key={i} className="border-t border-emerald-100">
                    <td className="py-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-green-600 text-white text-xs rounded font-bold flex items-center justify-center">
                        {i + 1}
                      </div>
                    </td>
                    <td className="py-2 font-semibold text-slate-700">{p}</td>
                    <td className="py-2 text-center">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold text-xs">{i === 0 ? 2 : i === 1 ? 1 : 0}</span>
                    </td>
                    <td className="py-2 text-center">
                      <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded font-bold text-xs">{i === 3 ? 2 : i === 2 ? 1 : 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Matches */}
          <div className="p-3 space-y-2">
            <p className="text-xs font-bold text-slate-500 px-1">משחקים</p>
            {group.matches.map((m, i) => (
              <div key={i} className={`border-r-4 ${m.completed ? 'border-emerald-500 bg-emerald-50/30' : 'border-slate-200'} pl-2 pr-3 py-2 rounded-l-lg`}>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 flex-1">
                    <span className={`${m.winner === 1 ? 'font-bold text-emerald-700' : 'text-slate-600'}`}>{m.p1}</span>
                    {m.winner === 1 && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                  </div>
                  {m.p1Scores ? (
                    <div className="flex gap-0.5 font-mono">
                      {m.p1Scores.map((s, idx) => (
                        <span key={idx} className={`w-5 text-center text-xs ${m.winner === 1 ? 'text-emerald-700 font-bold' : 'text-slate-400'}`}>{s}</span>
                      ))}
                    </div>
                  ) : <span className="text-xs text-slate-400">-</span>}
                </div>
                <div className="text-center text-[10px] text-slate-400 py-0.5">·</div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 flex-1">
                    <span className={`${m.winner === 2 ? 'font-bold text-emerald-700' : 'text-slate-600'}`}>{m.p2}</span>
                    {m.winner === 2 && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                  </div>
                  {m.p2Scores ? (
                    <div className="flex gap-0.5 font-mono">
                      {m.p2Scores.map((s, idx) => (
                        <span key={idx} className={`w-5 text-center text-xs ${m.winner === 2 ? 'text-emerald-700 font-bold' : 'text-slate-400'}`}>{s}</span>
                      ))}
                    </div>
                  ) : <span className="text-xs text-slate-400">-</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// OPTION 3 — Glass Premium (זכוכית פרימיום)
// ============================================================
const GlassPremium = () => (
  <div className="relative p-8 rounded-2xl overflow-hidden" dir="rtl" style={{
    background: 'linear-gradient(135deg, #dbeafe 0%, #ddd6fe 50%, #d1fae5 100%)'
  }}>
    <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/30 rounded-full blur-3xl" />
    <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-400/30 rounded-full blur-3xl" />
    
    <div className="relative">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-black" style={{
          background: 'linear-gradient(135deg, #2563eb, #10b981)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          🎾 שלב הבתים
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {GROUPS.map((group, gIdx) => (
          <div key={gIdx} className="bg-white/40 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/60 shadow-xl">
            {/* Floating header */}
            <div className="px-6 py-5 border-b border-white/40 bg-gradient-to-r from-white/60 to-white/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg" style={{
                    background: 'linear-gradient(135deg, #3b82f6, #10b981)'
                  }}>
                    {group.name}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-bold">בית</p>
                    <p className="text-xl font-black text-slate-800">קבוצה {group.name}</p>
                  </div>
                </div>
                <Trophy className="w-8 h-8 text-amber-400 opacity-60" />
              </div>
            </div>
            
            {/* Players */}
            <div className="p-4 grid grid-cols-2 gap-2 bg-white/30">
              {group.players.map((p, i) => (
                <div key={i} className="bg-white/60 backdrop-blur rounded-xl px-3 py-2 flex items-center gap-2 border border-white/60">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 text-white text-xs font-black flex items-center justify-center">
                    {i + 1}
                  </div>
                  <span className="text-xs font-bold text-slate-700 truncate">{p}</span>
                </div>
              ))}
            </div>
            
            {/* Matches */}
            <div className="p-4 space-y-3 bg-white/20">
              {group.matches.map((m, i) => (
                <div key={i} className="bg-white/70 backdrop-blur-md rounded-xl p-3 border border-white/80 shadow-sm hover:scale-[1.02] transition-transform">
                  <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-2 ${m.winner === 1 ? 'text-emerald-700 font-bold' : 'text-slate-600'}`}>
                      <span className="text-sm">{m.p1}</span>
                      {m.winner === 1 && <span className="text-xs">👑</span>}
                    </div>
                    {m.p1Scores && (
                      <div className="flex gap-1">
                        {m.p1Scores.map((s, idx) => (
                          <span key={idx} className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-mono font-bold ${
                            m.winner === 1 ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow' : 'bg-white/80 text-slate-500'
                          }`}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="my-1.5 flex items-center gap-2">
                    <div className="h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent flex-1" />
                    <span className="text-[9px] text-blue-500 font-bold tracking-widest">VS</span>
                    <div className="h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent flex-1" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-2 ${m.winner === 2 ? 'text-emerald-700 font-bold' : 'text-slate-600'}`}>
                      <span className="text-sm">{m.p2}</span>
                      {m.winner === 2 && <span className="text-xs">👑</span>}
                    </div>
                    {m.p2Scores && (
                      <div className="flex gap-1">
                        {m.p2Scores.map((s, idx) => (
                          <span key={idx} className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-mono font-bold ${
                            m.winner === 2 ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow' : 'bg-white/80 text-slate-500'
                          }`}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// OPTION 4 — Compact Pro (קומפקטי-מקצועי)
// ============================================================
const CompactPro = () => (
  <div className="bg-slate-50 p-8 rounded-2xl" dir="rtl">
    <div className="mb-6 flex items-baseline justify-between border-b-2 border-slate-200 pb-3">
      <div>
        <p className="text-xs tracking-[0.2em] text-slate-400 uppercase font-bold">Group Stage</p>
        <h2 className="text-2xl font-black text-slate-900">שלב הבתים</h2>
      </div>
      <div className="text-left text-xs text-slate-500">
        <p>{GROUPS.length} בתים · {GROUPS.length * 6} משחקים</p>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {GROUPS.map((group, gIdx) => (
        <div key={gIdx} className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-lg transition-shadow">
          {/* Compact header */}
          <div className="px-4 py-2.5 border-b-2 border-blue-500 bg-gradient-to-r from-blue-50 to-transparent flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 bg-blue-500 text-white rounded-md flex items-center justify-center font-black text-sm">{group.name}</span>
              <span className="font-bold text-slate-800">בית {group.name}</span>
            </div>
            <div className="flex gap-3 text-xs text-slate-500">
              <span>👥 {group.players.length}</span>
              <span>🎾 {group.matches.length}/6</span>
            </div>
          </div>
          
          {/* Players strip */}
          <div className="px-3 py-2 bg-slate-50/50 border-b border-slate-100">
            <div className="flex flex-wrap gap-1.5">
              {group.players.map((p, i) => (
                <div key={i} className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-0.5 text-xs">
                  <span className="w-4 h-4 bg-blue-500 text-white rounded-sm text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="font-medium text-slate-700">{p}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Matches - compact rows */}
          <div className="divide-y divide-slate-100">
            {group.matches.map((m, i) => (
              <div key={i} className={`px-3 py-2 hover:bg-slate-50 transition-colors ${m.completed ? '' : 'bg-amber-50/30'}`}>
                <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center text-sm">
                  <div className={`text-right ${m.winner === 1 ? 'font-bold text-emerald-700' : 'text-slate-700'}`}>
                    {m.p1}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {m.p1Scores && (
                      <span className={`px-1.5 py-0.5 rounded font-mono text-xs font-bold ${m.winner === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {m.p1Scores.join('-')}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-300 font-bold">vs</span>
                    {m.p2Scores && (
                      <span className={`px-1.5 py-0.5 rounded font-mono text-xs font-bold ${m.winner === 2 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {m.p2Scores.join('-')}
                      </span>
                    )}
                    {!m.completed && (
                      <span className="text-[10px] text-amber-600 font-bold">●</span>
                    )}
                  </div>
                  <div className={`text-left ${m.winner === 2 ? 'font-bold text-emerald-700' : 'text-slate-700'}`}>
                    {m.p2}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// OPTION 5 — Match-Day Cards (משחק של היום - הראש בראש)
// ============================================================
const MatchDayCards = () => (
  <div className="bg-gradient-to-br from-blue-50 via-white to-emerald-50 p-8 rounded-2xl" dir="rtl">
    <div className="text-center mb-8">
      <h2 className="text-3xl font-black text-slate-900">⚔️ קרבות הבתים</h2>
      <p className="text-slate-500 mt-1">Head-to-Head Matchups</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {GROUPS.map((group, gIdx) => (
        <div key={gIdx} className="bg-white rounded-2xl overflow-hidden shadow-xl border border-slate-100">
          {/* Header with diagonal split */}
          <div className="relative h-16 overflow-hidden">
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #3b82f6 50%, #10b981 50%, #10b981 100%)'
            }} />
            <div className="relative h-full flex items-center justify-center">
              <div className="bg-white rounded-full px-6 py-1.5 shadow-2xl">
                <span className="text-2xl font-black bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                  בית {group.name}
                </span>
              </div>
            </div>
          </div>
          
          {/* Players grid */}
          <div className="p-4 grid grid-cols-2 gap-2 bg-gradient-to-b from-slate-50 to-white">
            {group.players.map((p, i) => (
              <div key={i} className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
                <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white font-black mb-1 shadow-md">
                  {p.charAt(0)}
                </div>
                <p className="text-xs font-bold text-slate-700 truncate">{p}</p>
                <p className="text-[10px] text-slate-400">שחקן #{i + 1}</p>
              </div>
            ))}
          </div>
          
          {/* H2H Match Cards */}
          <div className="p-4 space-y-3 bg-slate-50">
            {group.matches.map((m, i) => (
              <div key={i} className={`bg-white rounded-2xl overflow-hidden shadow-md border ${
                m.completed ? 'border-emerald-200' : 'border-slate-200'
              }`}>
                <div className="grid grid-cols-[1fr_auto_1fr]">
                  {/* P1 side */}
                  <div className={`p-3 ${m.winner === 1 ? 'bg-gradient-to-l from-emerald-50 to-transparent' : ''}`}>
                    <div className="text-center">
                      <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-black text-white mb-1 ${
                        m.winner === 1 ? 'bg-gradient-to-br from-emerald-500 to-green-600 ring-2 ring-emerald-300' : 'bg-slate-400'
                      }`}>
                        {m.p1.charAt(0)}
                      </div>
                      <p className={`text-xs font-bold ${m.winner === 1 ? 'text-emerald-700' : 'text-slate-700'}`}>{m.p1}</p>
                      {m.winner === 1 && <p className="text-[10px] text-emerald-600 font-bold mt-0.5">🏆 מנצח</p>}
                    </div>
                    {m.p1Scores && (
                      <div className="flex justify-center gap-1 mt-2">
                        {m.p1Scores.map((s, idx) => (
                          <span key={idx} className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-mono font-bold ${
                            m.winner === 1 ? 'bg-emerald-500 text-white shadow' : 'bg-slate-100 text-slate-500'
                          }`}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* VS center */}
                  <div className="flex flex-col items-center justify-center px-2 bg-gradient-to-b from-slate-50 to-slate-100 border-x border-slate-200">
                    <span className="text-xl font-black text-slate-300">VS</span>
                    {m.completed ? (
                      <span className="text-[9px] text-emerald-600 font-bold mt-1">✓ הסתיים</span>
                    ) : (
                      <span className="text-[9px] text-amber-600 font-bold mt-1">⏳ ממתין</span>
                    )}
                  </div>
                  
                  {/* P2 side */}
                  <div className={`p-3 ${m.winner === 2 ? 'bg-gradient-to-r from-emerald-50 to-transparent' : ''}`}>
                    <div className="text-center">
                      <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-black text-white mb-1 ${
                        m.winner === 2 ? 'bg-gradient-to-br from-emerald-500 to-green-600 ring-2 ring-emerald-300' : 'bg-slate-400'
                      }`}>
                        {m.p2.charAt(0)}
                      </div>
                      <p className={`text-xs font-bold ${m.winner === 2 ? 'text-emerald-700' : 'text-slate-700'}`}>{m.p2}</p>
                      {m.winner === 2 && <p className="text-[10px] text-emerald-600 font-bold mt-0.5">🏆 מנצח</p>}
                    </div>
                    {m.p2Scores && (
                      <div className="flex justify-center gap-1 mt-2">
                        {m.p2Scores.map((s, idx) => (
                          <span key={idx} className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-mono font-bold ${
                            m.winner === 2 ? 'bg-emerald-500 text-white shadow' : 'bg-slate-100 text-slate-500'
                          }`}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// MAIN PAGE
// ============================================================
const OPTIONS = [
  { key: 'stadium', label: '🏟️ Stadium Premium', component: StadiumPremium, desc: 'אצטדיון פרימיום: כותרות עם גרדיאנט כחול, אווטרים עם ראשי תיבות, פסי ניצחון, מאוד "ספורט גדול".' },
  { key: 'scorecard', label: '📋 Tennis Scorecard', component: TennisScorecard, desc: 'כרטיס ניקוד טניס קלאסי: טבלת ניצחונות/הפסדים, progress bar להתקדמות הבית, מקצועי וברור.' },
  { key: 'glass', label: '💎 Glass Premium', component: GlassPremium, desc: 'גלאס-מורפיזם פרימיום: רקע כחול-ירוק מטושטש, כרטיסים שקופים, אווירה מודרנית 2026.' },
  { key: 'compact', label: '⚡ Compact Pro', component: CompactPro, desc: 'קומפקטי מקצועי: צפיפות מידע גבוהה, ניצול מקסימלי של מסך, מתאים למסכים קטנים.' },
  { key: 'matchday', label: '⚔️ Match-Day Cards', component: MatchDayCards, desc: 'כרטיסי קרב: אווטרים עגולים, מסך מפוצל באלכסון, תחושת "קרב אישי" בין שחקנים.' },
];

const GroupsPreviewPage = () => {
  const [selected, setSelected] = useState('stadium');
  const Selected = OPTIONS.find(o => o.key === selected);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" dir="rtl">
      <div className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-slate-900">🎨 הדמיית עיצוב לבתים ומשחקים</h1>
              <p className="text-sm text-slate-500">בחר/י את הסגנון שאהבת ביותר ואני אבנה אותו באתר.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSelected(opt.key)}
                  data-testid={`groups-option-${opt.key}`}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    selected === opt.key
                      ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-lg scale-105'
                      : 'bg-white border border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900"><strong>{Selected.label}:</strong> {Selected.desc}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <Selected.component />
      </div>

      <div className="max-w-7xl mx-auto p-6 pb-12">
        <div className="bg-gradient-to-r from-blue-600 to-emerald-600 rounded-2xl p-8 text-center text-white shadow-xl">
          <h3 className="text-2xl font-black mb-2">בחרת? 🎯</h3>
          <p className="text-blue-100 mb-4">כתוב/י לי בצ׳אט איזה סגנון אהבת ואני אבנה אותו באתר תוך כמה דקות.</p>
          <div className="flex flex-wrap justify-center gap-3 text-sm font-bold">
            <span className="px-4 py-2 bg-white/20 rounded-full">"Stadium Premium"</span>
            <span className="px-4 py-2 bg-white/20 rounded-full">"Tennis Scorecard"</span>
            <span className="px-4 py-2 bg-white/20 rounded-full">"Glass Premium"</span>
            <span className="px-4 py-2 bg-white/20 rounded-full">"Compact Pro"</span>
            <span className="px-4 py-2 bg-white/20 rounded-full">"Match-Day Cards"</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupsPreviewPage;
